import { PlayerProjection } from '../types';

/**
 * Faster than lodash/isEqual, purpose-built equality check for `PlayerProjection[]`.
 *
 * Properties are compared in rough order of how often they change, so a changed
 * projection bails out after a couple of comparisons instead of descending into nested data.
 *
 * Recent stats are ignored, as those will not change often.
 */

type AllowedOptions = PlayerProjection['allowedOptions'];

const areAllowedOptionsEqual = (first: AllowedOptions, second: AllowedOptions): boolean => {
    if (first === second) {
        return true;
    }
    if (!first || !second) {
        return false;
    }
    if (first.length !== second.length) {
        return false;
    }
    for (let i = 0; i < first.length; i++) {
        if (first[i].marketOptionId !== second[i].marketOptionId || first[i].outcome !== second[i].outcome) {
            return false;
        }
    }
    return true;
};

const isProjectionEqual = (first: PlayerProjection, second: PlayerProjection): boolean => {
    if (first === second) {
        return true;
    }
    if (!first || !second) {
        return false;
    }

    //Only look at fields that are relevant to the app when comparing 2 projections
    //Ignoring fields like label, name, order etc. that are not used, and will not change often
    if (
        first.marketId !== second.marketId ||
        first.value !== second.value ||
        first.currentValue !== second.currentValue ||
        first.marketStatus !== second.marketStatus ||
        first.type !== second.type ||
        first.key !== second.key ||
        first.nonRegularValue !== second.nonRegularValue ||
        first.nonRegularPercentage !== second.nonRegularPercentage ||
        first.isLive !== second.isLive
    ) {
        return false;
    }

    return areAllowedOptionsEqual(first.allowedOptions, second.allowedOptions);
};

/**
 * Deep-compares two lists of player projections.
 *
 * Matches `isEqual` semantics for the inputs this is used with: two `undefined` lists are equal,
 * one `undefined` list is never equal to a defined one.
 */
export const arePlayerProjectionsEqual = (
    first: PlayerProjection[] | undefined,
    second: PlayerProjection[] | undefined
): boolean => {
    if (first === second) {
        return true;
    }
    if (!first || !second) {
        return false;
    }
    if (first.length !== second.length) {
        return false;
    }
    for (let i = 0; i < first.length; i++) {
        if (!isProjectionEqual(first[i], second[i])) {
            return false;
        }
    }
    return true;
};
